import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const CompanyStats = () => {
  const [tagStats, setTagStats] = useState([]);
  const [cityStats, setCityStats] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tagLimit, setTagLimit] = useState(20);
  const [cityLimit, setCityLimit] = useState(15);
  const API = useContext(ApiContext);

  // Couleurs pour le pie chart
  const COLORS = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1',
    '#d084d0', '#ffb347', '#87ceeb', '#deb887', '#f0e68c',
    '#ffa07a', '#20b2aa', '#b0c4de', '#ffb6c1', '#98fb98'
  ];

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');

      const [tagsResponse, citiesResponse, summaryResponse] = await Promise.all([
        API.get(`/api/individuals-by-tags?limit=${tagLimit}&minCount=1`),
        API.get(`/api/individuals-by-cities?limit=${cityLimit}&minCount=1`),
        API.get('/api/summary')
      ]);

      setTagStats(tagsResponse.data.data || []);
      setCityStats(citiesResponse.data.data || []);
      setSummary(summaryResponse.data.data || {});

    } catch (error) {
      console.error('Erreur lors du chargement des analytics:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [tagLimit, cityLimit]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded shadow-lg">
          <p className="font-medium">{`${label}`}</p>
          <p className="text-blue-600">
            {`Candidats: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-3 border rounded shadow-lg">
          <p className="font-medium">{data.name}</p>
          <p className="text-blue-600">
            {`Candidats: ${data.value}`}
          </p>
          <p className="text-gray-600">
            {`${((data.value / cityStats.reduce((sum, city) => sum + city.count, 0)) * 100).toFixed(1)}%`}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-10 min-h-screen pt-4 sm:pt-8 lg:pt-16">
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-slate-200 border-t-indigo-500 rounded-full animate-spin"></div>
          <span className="text-base sm:text-lg">Chargement des analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-2 sm:px-0">
      <header className="mb-4 sm:mb-6 lg:mb-8">
        <p className="text-sm sm:text-base text-slate-600">Visualisez les données de votre entreprise.</p>
      </header>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Résumé */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <h3 className="text-sm sm:text-lg font-medium text-slate-600 mb-2">Total Candidats</h3>
          <p className="text-2xl sm:text-3xl font-bold text-blue-600">{summary.totalIndividuals || 0}</p>
        </div>
        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <h3 className="text-sm sm:text-lg font-medium text-slate-600 mb-2">Tags Uniques</h3>
          <p className="text-2xl sm:text-3xl font-bold text-green-600">{summary.totalUniqueTags || 0}</p>
        </div>
        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <h3 className="text-sm sm:text-lg font-medium text-slate-600 mb-2">Villes Uniques</h3>
          <p className="text-2xl sm:text-3xl font-bold text-purple-600">{summary.totalUniqueCities || 0}</p>
        </div>
        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <h3 className="text-sm sm:text-lg font-medium text-slate-600 mb-2">Taux de Tagging</h3>
          <p className="text-2xl sm:text-3xl font-bold text-orange-600">{summary.tagsUsageRate || 0}%</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
        {/* Graphique par Tags */}
        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-2 sm:space-y-0">
            <h2 className="text-lg sm:text-xl font-semibold text-slate-800">Candidats par Tag</h2>
            <div className="flex items-center space-x-2">
              <label className="text-xs sm:text-sm text-slate-600">Afficher:</label>
              <select 
                value={tagLimit} 
                onChange={(e) => setTagLimit(parseInt(e.target.value))}
                className="border rounded px-2 py-1 text-xs sm:text-sm"
              >
                <option value={10}>Top 10</option>
                <option value={15}>Top 15</option>
                <option value={20}>Top 20</option>
                <option value={30}>Top 30</option>
              </select>
            </div>
          </div>

          {tagStats.length > 0 ? (
            <div className="h-72 sm:h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={tagStats}
                  margin={{ top: 20, right: 10, left: 10, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="tagName" 
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    fontSize={10}
                    className="text-xs"
                  />
                  <YAxis fontSize={10} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#8884d8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-500">
              Aucune donnée de tags disponible
            </div>
          )}
        </div>

        {/* Graphique par Villes */}
        <div className="bg-white p-4 sm:p-6 rounded-lg border">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-2 sm:space-y-0">
            <h2 className="text-lg sm:text-xl font-semibold text-slate-800">Candidats par Ville</h2>
            <div className="flex items-center space-x-2">
              <label className="text-xs sm:text-sm text-slate-600">Afficher:</label>
              <select 
                value={cityLimit} 
                onChange={(e) => setCityLimit(parseInt(e.target.value))}
                className="border rounded px-2 py-1 text-xs sm:text-sm"
              >
                <option value={10}>Top 10</option>
                <option value={15}>Top 15</option>
                <option value={20}>Top 20</option>
              </select>
            </div>
          </div>

          {cityStats.length > 0 ? (
            <div className="h-72 sm:h-96">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={cityStats.map(city => ({ name: city.city, value: city.count }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent, index }) => {
                      // Afficher les labels seulement pour les 5 premières villes (les plus présentes)
                      if (index < 3 && window.innerWidth >= 640) {
                        return `${name} (${(percent * 100).toFixed(0)}%)`;
                      }
                      return '';
                    }}
                    labelLine={false}
                  >
                    {cityStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    wrapperStyle={{ fontSize: '12px' }}
                    formatter={(value) => value.length > 12 ? `${value.substring(0, 12)}...` : value}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-500">
              Aucune donnée de villes disponible
            </div>
          )}
        </div>
      </div>

      
    </div>
  );
};

export default CompanyStats;